/**
 * A faixa do escopo — onda 268 (§9.1 da ideia do fluxo da OS).
 *
 * O escopo contratado por método aparecia de três jeitos: uma tabela de
 * cinco colunas na aba Escopo, uma fileira de «UMID 4/10» acima da grade
 * da Programação e um «usados/contratados» no cabeçalho de cada coluna.
 * Três desenhos para a mesma conta. Aqui fica UM: método · nome · barra
 * programados/contratados · saldo — em versão cheia (aba Escopo) e
 * compacta (Programação, fecho do recebimento). O pacote diz quantos
 * corpos de prova cada ensaio leva («CD3.IN · 3 CPs», 0210).
 *
 * O saldo nunca é só cor (§18): vem escrito («cabem 4», «esgotado», «2 a
 * mais»), e a barra tem o número ao lado.
 *
 * A conta não é feita aqui. Quem chama entrega o que o servidor disse
 * (`vw_os_escopo_por_metodo`: contratados, programados, por chegar) e, na
 * Programação, o que está marcado na tela e ainda não foi gravado.
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';

export interface LinhaDoEscopo {
  id: string;
  codigo: string;
  nome?: string | null;
  contratada: number;
  /** Ensaios vivos (sem cancelado nem substituído). */
  programados: number;
  /** Marcados em amostra por chegar (0220, D6). */
  reservados?: number;
  /** Marcados na tela e ainda não gravados (Programação). */
  marcadas?: number;
  cancelados?: number;
  /** CPs por ensaio, quando o item é pacote (0210). */
  cps?: number | null;
}

/** O que vem do servidor (`escopo_por_metodo` da OS ou `contratado` da grade). */
export interface EscopoDoServidor {
  metodo_id: string;
  metodo_codigo: string;
  metodo_nome?: string | null;
  quantidade_contratada: string | number;
  programados: number;
  cancelados?: number;
  reservados?: number;
  cps_por_ensaio?: number | null;
}

export const linhaDoServidor = (c: EscopoDoServidor): LinhaDoEscopo => ({
  id: c.metodo_id,
  codigo: c.metodo_codigo,
  nome: c.metodo_nome ?? null,
  contratada: Number(c.quantidade_contratada),
  programados: Number(c.programados ?? 0),
  reservados: Number(c.reservados ?? 0),
  cancelados: Number(c.cancelados ?? 0),
  cps: c.cps_por_ensaio ?? null,
});

/** Usados = programados + por chegar + marcados agora. */
export const usadosDa = (l: LinhaDoEscopo) =>
  l.programados + (l.reservados ?? 0) + (l.marcadas ?? 0);

type TomDoSaldo = 'folgado' | 'no-limite' | 'estourado';

export function saldoDa(l: LinhaDoEscopo): { saldo: number; tom: TomDoSaldo; palavra: string } {
  const saldo = l.contratada - usadosDa(l);
  const fmt = (n: number) => n.toLocaleString('pt-BR');
  if (saldo < 0) return { saldo, tom: 'estourado', palavra: `${fmt(-saldo)} a mais` };
  if (saldo === 0) return { saldo, tom: 'no-limite', palavra: 'esgotado' };
  return { saldo, tom: 'folgado', palavra: `cabem ${fmt(saldo)}` };
}

const frase = (l: LinhaDoEscopo) => {
  const u = usadosDa(l);
  const { saldo } = saldoDa(l);
  return saldo < 0
    ? `${u} programados para ${l.contratada} contratados — ${-saldo} a mais. `
      + 'Para programar mais, inclua o ensaio na OS (revisão).'
    : saldo === 0
      ? `${u} de ${l.contratada} contratados — escopo esgotado`
      : `${u} de ${l.contratada} contratados — cabem mais ${saldo}`;
};

/** O código do método, com o pacote quando há. */
export function CodigoDoMetodo({ l }: { l: LinhaDoEscopo }) {
  return (
    <span className="faixa-codigo">
      <span className="mono">{l.codigo}</span>
      {l.cps && l.cps > 1 && <span className="faixa-pacote"> · {l.cps} CPs</span>}
    </span>
  );
}

/**
 * A barra programados/contratados. Três trechos: gravado (cheio), por
 * chegar (listrado) e marcado agora (contorno). O excedente passa da
 * borda e a barra ganha a marca de estouro.
 */
export function BarraDoEscopo({ l }: { l: LinhaDoEscopo }) {
  const total = Math.max(l.contratada, usadosDa(l), 1);
  const pc = (n: number) => `${(100 * n) / total}%`;
  const { tom } = saldoDa(l);
  return (
    <span className={`barra-escopo ${tom}`} aria-hidden="true">
      <span className="trecho gravado" style={{ width: pc(l.programados) }} />
      {(l.reservados ?? 0) > 0 && (
        <span className="trecho por-chegar" style={{ width: pc(l.reservados ?? 0) }} />
      )}
      {(l.marcadas ?? 0) > 0 && (
        <span className="trecho marcado" style={{ width: pc(l.marcadas ?? 0) }} />
      )}
      {usadosDa(l) > l.contratada && (
        <span className="limite" style={{ left: pc(l.contratada) }} />
      )}
    </span>
  );
}

/** O número «usados/contratados», com o excedente — o chip da coluna. */
export function SaldoDoMetodo({ l }: { l: LinhaDoEscopo }) {
  const { saldo, tom } = saldoDa(l);
  return (
    <span className={`saldo-col ${tom}`} title={`${l.codigo}: ${frase(l)}`}>
      {usadosDa(l)}/{l.contratada}
      {saldo < 0 && <span className="excede"> +{-saldo}</span>}
    </span>
  );
}

export function FaixaDoEscopo({ linhas, compacta, fora, rotulo, rodape }: {
  linhas: LinhaDoEscopo[];
  /** Chips numa linha só (Programação, fecho do recebimento). */
  compacta?: boolean;
  /** Métodos em jogo que não estão no escopo — o banco recusa. */
  fora?: { id: string; codigo: string }[];
  rotulo?: string;
  rodape?: ReactNode;
}) {
  if (!linhas.length && !fora?.length) return null;
  if (compacta) {
    return (
      <section className="faixa-escopo compacta faixa-saldo"
        aria-label={rotulo ?? 'Saldo do escopo contratado'}>
        <span className="rot">{rotulo ?? 'Escopo contratado'}</span>
        {linhas.map((l) => (
          <span key={l.id} className="item-saldo item-escopo" title={`${l.nome ?? l.codigo}: ${frase(l)}`}>
            <CodigoDoMetodo l={l} />
            <BarraDoEscopo l={l} />
            <SaldoDoMetodo l={l} />
          </span>
        ))}
        {fora?.map((m) => (
          <span key={m.id} className="item-saldo item-escopo fora-do-escopo"
            title={`${m.codigo} não está no escopo contratado desta OS: programar é recusado`}>
            <span className="mono">{m.codigo}</span>
            <span className="saldo-col estourado">fora do escopo</span>
          </span>
        ))}
        {rodape && <span className="sub">{rodape}</span>}
      </section>
    );
  }
  return (
    <div className="faixa-escopo cheia">
      <table className="dados">
        <thead>
          <tr>
            <th scope="col">Ensaio</th>
            <th scope="col">Programados / Contratados</th>
            <th scope="col" className="num">Por chegar</th>
            <th scope="col" className="num">Saldo</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => {
            const s = saldoDa(l);
            return (
              <tr key={l.id} data-metodo={l.codigo}>
                <th scope="row">
                  <CodigoDoMetodo l={l} />
                  {l.nome && <div className="sub">{l.nome}</div>}
                </th>
                <td className="faixa-barra">
                  <BarraDoEscopo l={l} />
                  {/* 332 (D10): a marca por chegar conta — a barra e o saldo já
                      contavam; o número dizia «0 de 1» ao lado da barra cheia. */}
                  <span className="mono faixa-numeros">
                    {l.programados + (l.reservados ?? 0)} de {l.contratada.toLocaleString('pt-BR')}
                  </span>
                  {(l.cancelados ?? 0) > 0 && (
                    <span className="sub"> ({l.cancelados} cancelado{(l.cancelados ?? 0) > 1 ? 's' : ''})</span>
                  )}
                </td>
                <td className="num mono">{l.reservados ?? 0}</td>
                <td className={`num faixa-saldo-palavra ${s.tom}`}>{s.palavra}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rodape && <p className="sub">{rodape}</p>}
    </div>
  );
}

/**
 * A faixa do escopo de uma OS, buscada pelo id — para as telas que não
 * carregam a OS inteira (a ficha do recebimento, o fecho do assistente).
 * O recebimento é onde o escopo começa a ser gasto: a amostra que chega
 * ligada a uma prevista traz os ensaios marcados nela.
 */
export function EscopoDaOs({ osId, rotulo }: { osId: string; rotulo?: string }) {
  const [linhas, setLinhas] = useState<LinhaDoEscopo[] | null>(null);
  useEffect(() => {
    let vivo = true;
    api<{ escopo_por_metodo?: EscopoDoServidor[] }>(`/os/${osId}`)
      .then((os) => { if (vivo) setLinhas((os.escopo_por_metodo ?? []).map(linhaDoServidor)); })
      .catch(() => { if (vivo) setLinhas([]); });
    return () => { vivo = false; };
  }, [osId]);
  if (!linhas || !linhas.length) return null;
  return (
    <FaixaDoEscopo compacta linhas={linhas} rotulo={rotulo ?? 'Escopo da OS'}
      rodape={<>o que já tem ensaio ou está marcado em amostra por chegar ·{' '}
        <Link to={`/os/${osId}/escopo`}>ver o escopo</Link></>} />
  );
}
